/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  category: 'ALL',
  subcat: 'ALL',
  supplier: 'ALL',
  order_by: 'tb.code',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

function SearchQuotation({ callback }) {
  const mod = 'production';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalOpen, setModalOpen] = useState(false);
  const txtSearch = useRef(null);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.status', label: 'สถานะ', align: 'center' },
    { id: 'tb.code', label: 'ใบสั่งซื้อ', align: 'center' },
    { id: 'tb.group_id', label: 'กลุ่มสั่งซื้อ', align: 'center' },
    { id: 'tb.supplier_id', label: 'ผู้จัดจำหน่าย', align: 'left' },
    { id: 'tb.branch_receive', label: 'สาขารับเข้า', align: 'left' },
    { id: 'tb.remark', label: 'หมายเหตุ', align: 'left' },
    { id: 'tb.user_id', label: 'ผู้ทำรายการ', align: 'left' },
    { id: 'tb.nettotal', label: 'ราคาซื้อ', align: 'right' },
    { id: 'tb.deposit', label: 'มัดจำ', align: 'right' },
    { id: 'tb.date_record', label: 'วันสั่งซื้อ', align: 'center' },
    { id: 'tb.date_due', label: 'กำหนดรับ', align: 'center' },
    { id: 'open', label: '', align: 'center', sort: false },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('OPTIONS', `/${mod}/quotation`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      supplier: data.supplier,
      subcat: data.subcat,
      ref_id: id,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const btnOpen = (item) => {
    return (
      <MDButton
        variant='text'
        color='info'
        size='small'
        sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}
        onClick={() => {
          setModalOpen(false);
          callback({ quotation_id: item.id });
        }}
      >
        {item.code}
      </MDButton>
    );
  };

  const btnGroup = (item) => {
    if (item.is_group === 'N') return '-';

    return (
      <MDButton
        variant='text'
        color='error'
        size='small'
        sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}
        onClick={() => {
          setModalOpen(false);
          callback({ po_id: 0, group_id: item.group_id, is_group: 'Y' });
        }}
      >
        {item.group_code}
      </MDButton>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const renderSearch = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  inputRef: txtSearch,
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 0, minHeight: 500 }}>
              <Table aria-label='simple table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center' width={160}>
                        {Func.statusDisplay(row)}
                      </TableCell>
                      <TableCell align='center'>{btnOpen(row)}</TableCell>
                      <TableCell align='center'>{btnGroup(row)}</TableCell>
                      <TableCell>
                        <MDBox>{row.supplier_code}</MDBox>
                        <MDBox className='cut' width={150}>
                          {row.supplier_name}
                        </MDBox>
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.branch_code}
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.remark}
                      </TableCell>
                      <TableCell align='left'>{row.user_name}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.nettotal)}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.deposit)}</TableCell>
                      <TableCell align='center'>{Func.dateDisplay(row.date_record)}</TableCell>
                      <TableCell align='center'>{Func.dateDisplay(row.date_due)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
              <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                {t(`element.show_record`, { num: find.perPage })}&nbsp;
                <Icon>keyboard_arrow_down</Icon>
              </MDButton>
              {renderShow}
              <Pagination
                count={pageTotal}
                variant='outlined'
                color='secondary'
                showFirstButton
                showLastButton
                page={pageNo}
                onChange={(e, p) => view({ ...find, page: p })}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={() => setModalOpen(false)}
      >
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label='close'
            onClick={() => setModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderList()}</DialogContent>
      </Dialog>
    );
  };

  return (
    <div>
      <MDButton
        variant='outlined'
        tabIndex={-1}
        color='dark'
        size='small'
        iconOnly
        onClick={() => {
          setModalOpen(true);
          view(find);

          setTimeout(function () {
            txtSearch.current.focus();
          }, 500);
        }}
      >
        <Icon>search</Icon>
      </MDButton>

      {renderOpen()}
      {notify}
    </div>
  );
}

SearchQuotation.propTypes = {
  callback: PropTypes.func.isRequired,
};

export default SearchQuotation;
